import { reactive } from 'vue'
import { ApiError, api, deleteJson, patchJson, standaloneMode } from './api'
import { auth } from './state'

export interface MusicTrack {
  id: string
  title: string
  originalName: string
  mimeType: string
  sizeBytes: number
  createdAt: string
  streamUrl: string
}

interface MusicLibraryResponse {
  tracks: MusicTrack[]
  selectedTrackId: string | null
  volume: number
  limits: { maxTracks: number; maxTrackBytes: number; maxLibraryBytes: number }
}

export const music = reactive({
  panelOpen: false,
  loaded: false,
  loading: false,
  uploading: false,
  playing: false,
  selectedTrackId: null as string | null,
  volume: 35,
  tracks: [] as MusicTrack[],
  message: '',
  error: '',
  limits: { maxTracks: 12, maxTrackBytes: 20 * 1024 * 1024, maxLibraryBytes: 100 * 1024 * 1024 },
})

let audio: HTMLAudioElement | null = null
let loadedUserId: string | null = null
let activeTrackId: string | null = null
let volumeTimer: number | undefined

function ensureAudio() {
  if (audio) return audio
  audio = new Audio()
  audio.loop = true
  audio.preload = 'metadata'
  audio.volume = music.volume / 100
  audio.addEventListener('play', () => { music.playing = true })
  audio.addEventListener('pause', () => { music.playing = false })
  audio.addEventListener('error', () => {
    music.playing = false
    music.error = '音乐暂时无法播放，请检查文件格式或重新上传。'
  })
  return audio
}

function prepareTrack(id: string | null) {
  if (!id) return null
  const track = music.tracks.find((item) => item.id === id)
  if (!track) return null
  const player = ensureAudio()
  if (activeTrackId !== id) {
    player.pause()
    player.src = track.streamUrl
    player.load()
    activeTrackId = id
  }
  player.volume = music.volume / 100
  return player
}

function resetLibrary() {
  audio?.pause()
  if (audio) audio.removeAttribute('src')
  activeTrackId = null
  loadedUserId = null
  music.loaded = false
  music.loading = false
  music.playing = false
  music.selectedTrackId = null
  music.tracks = []
  music.message = ''
  music.error = ''
}

export async function loadMusicLibrary(force = false) {
  if (standaloneMode || !auth.user) {
    resetLibrary()
    music.loaded = true
    return
  }
  if (!force && music.loaded && loadedUserId === auth.user.id) return
  music.loading = true
  music.error = ''
  try {
    const result = await api<MusicLibraryResponse>('/api/music')
    music.tracks = result.tracks
    music.selectedTrackId = result.selectedTrackId
    music.volume = result.volume
    music.limits = result.limits
    loadedUserId = auth.user.id
    music.loaded = true
    if (audio) audio.volume = music.volume / 100
  } catch (error) {
    music.error = error instanceof ApiError ? error.message : '私人音乐库加载失败。'
  } finally {
    music.loading = false
  }
}

export function openMusicLibrary() {
  music.panelOpen = true
  music.message = ''
  music.error = ''
  void loadMusicLibrary()
}

export function closeMusicLibrary() {
  music.panelOpen = false
}

async function savePreferences() {
  if (standaloneMode || !auth.user) return
  await patchJson('/api/music/preferences', { selectedTrackId: music.selectedTrackId, volume: music.volume })
}

export async function selectAndPlayTrack(id: string) {
  music.error = ''
  music.message = ''
  music.selectedTrackId = id
  const track = prepareTrack(id)
  if (!track) return
  void savePreferences().catch(() => { music.error = '音乐选择没有同步到服务器。' })
  try {
    await ensureAudio().play()
  } catch {
    music.playing = false
    music.error = '浏览器暂未允许播放，请再次点击播放按钮。'
  }
}

export async function toggleBackgroundMusic() {
  music.error = ''
  const player = ensureAudio()
  if (music.playing) {
    player.pause()
    return
  }
  const id = music.selectedTrackId || music.tracks[0]?.id
  if (!id) {
    music.error = '请先上传一首音乐。'
    return
  }
  music.selectedTrackId = id
  prepareTrack(id)
  void savePreferences().catch(() => undefined)
  try {
    await player.play()
  } catch {
    music.playing = false
    music.error = '浏览器暂未允许播放，请再次点击播放按钮。'
  }
}

export function setMusicVolume(value: number) {
  music.volume = Math.max(0, Math.min(100, Math.round(value)))
  if (audio) audio.volume = music.volume / 100
  window.clearTimeout(volumeTimer)
  volumeTimer = window.setTimeout(() => void savePreferences().catch(() => undefined), 350)
}

function normalizedMime(file: File) {
  const extension = file.name.toLowerCase().split('.').pop()
  if (extension === 'mp3') return 'audio/mpeg'
  if (extension === 'wav') return 'audio/wav'
  if (extension === 'ogg') return 'audio/ogg'
  if (extension === 'm4a') return 'audio/mp4'
  return file.type
}

export async function uploadMusic(file: File) {
  music.error = ''
  music.message = ''
  if (standaloneMode) {
    music.error = '单文件版没有后端存储，请在完整版网站中上传音乐。'
    return
  }
  if (!auth.user) {
    music.error = '登录后才能使用私人音乐库。'
    return
  }
  const mime = normalizedMime(file)
  if (!['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/x-m4a'].includes(mime)) {
    music.error = '请选择 MP3、WAV、OGG 或 M4A 文件。'
    return
  }
  if (file.size > music.limits.maxTrackBytes) {
    music.error = '单个音乐文件不能超过 20 MB。'
    return
  }
  music.uploading = true
  try {
    const title = file.name.replace(/\.[^.]+$/, '')
    const result = await api<{ track: MusicTrack }> (`/api/music?title=${encodeURIComponent(title)}&filename=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      headers: { 'Content-Type': mime },
      body: await file.arrayBuffer(),
    })
    music.tracks.unshift(result.track)
    if (!music.selectedTrackId) music.selectedTrackId = result.track.id
    music.message = '音乐已上传，点击曲目即可播放。'
  } catch (error) {
    music.error = error instanceof ApiError ? error.message : '音乐上传失败，请稍后重试。'
  } finally {
    music.uploading = false
  }
}

export async function removeMusicTrack(id: string) {
  const track = music.tracks.find((item) => item.id === id)
  if (!track || !window.confirm(`确定从私人音乐库删除“${track.title}”吗？删除后无法恢复。`)) return
  music.error = ''
  music.message = ''
  try {
    await deleteJson(`/api/music/${id}`)
    if (activeTrackId === id) {
      audio?.pause()
      if (audio) audio.removeAttribute('src')
      activeTrackId = null
    }
    music.tracks = music.tracks.filter((item) => item.id !== id)
    if (music.selectedTrackId === id) {
      music.selectedTrackId = music.tracks[0]?.id || null
      await savePreferences()
    }
    music.message = '音乐已从私人音乐库移除。'
  } catch (error) {
    music.error = error instanceof ApiError ? error.message : '删除失败，请稍后重试。'
  }
}

export function syncMusicAccount() {
  if (!auth.user || loadedUserId !== auth.user.id) resetLibrary()
  if (auth.user) void loadMusicLibrary()
}
